import { useState, useEffect } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import SEO from '../components/SEO'

const heroImages = [
  '/images/Hero-1.jpg',
  '/images/Hero-2.jpg',
  '/images/Hero-3.jpg',
  '/images/Hero-4.jpg',
  '/images/Hero-5.jpg',
  '/images/Hero-6.jpg',
  '/images/Hero-7.jpg',
  '/images/IMG_6451.JPG',
  '/images/IMG_6460.JPG',
  '/images/IMG_6479.JPG',
  '/images/000229740018.jpg',
  '/images/000229760018.jpg',
  '/images/000229770031.jpg',
  '/images/000022240002.jpg',
  '/images/000022240010.jpg',
  '/images/000022240018.jpg',
  '/images/000022240019.jpg',
  '/images/000022250036.jpg',
  '/images/000022250037.jpg',
  '/images/000022270006.jpg',
  '/images/000022270013.jpg',
  '/images/000022270019.jpg',
  '/images/000022270027.jpg',
  '/images/000022270035.jpg',
  '/images/000022340015.jpg',
]

export default function About({ onLoginClick }) {
  const [currentImage, setCurrentImage] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % heroImages.length)
    }, 4500)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="bg-black min-h-screen flex flex-col">
      <SEO
        title="About"
        description="Dottedline exists to stop football losing its meaning as it moves forward. Football culture first. Everything else comes second."
        url="/about"
      />

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <div className="relative h-screen w-full overflow-hidden flex-shrink-0">

        {/* Slideshow */}
        <div className="absolute inset-0">
          {heroImages.map((src, index) => (
            <img
              key={src}
              src={src}
              alt={`Dottedline ${index + 1}`}
              className={`hero-image ${index === currentImage ? 'active' : ''}`}
            />
          ))}
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Navbar */}
        <Navbar onLoginClick={onLoginClick} variant="dark" />

        {/* About text — bottom left */}
        <div className="relative h-full flex flex-col justify-end p-8 pb-16">
          <p className="text-white/60 text-xs uppercase tracking-widest mb-3 font-medium">
            About
          </p>
          <h1 className="text-white text-4xl md:text-6xl max-w-3xl leading-tight">
            Dottedline exists to stop football losing its meaning as it moves forward.
          </h1>
          <p className="text-white/70 text-lg md:text-xl mt-5 max-w-xl font-light">
            Football culture first. Everything else comes second.
          </p>
        </div>

        {/* Image indicators */}
        <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8 flex gap-1">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImage(index)}
              aria-label={`Go to image ${index + 1}`}
              className="p-2 flex items-center justify-center transition-all duration-300"
            >
              <span className={`block rounded-full transition-all duration-300 ${
                index === currentImage ? 'bg-white w-6 h-2' : 'bg-white/50 w-2 h-2'
              }`} />
            </button>
          ))}
        </div>
      </div>

      {/* ── CONTENT ──────────────────────────────────────────────── */}
      <div className="bg-white flex-1">
        <main className="pt-24 pb-24 px-6">
          <div className="max-w-3xl mx-auto">

            {/* The Mission */}
            <section className="mb-24 border-t border-gray-200 pt-12">
              <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-8">
                The Mission
              </h2>
              <p className="text-2xl font-semibold tracking-tight leading-tight mb-10">
                To document and grow football culture without erasing its roots.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">That means:</p>
              <ul className="space-y-4 text-lg text-gray-600">
                <li className="flex items-start">
                  <span className="mr-4 text-gray-300">—</span>
                  Remembering the past without living in it
                </li>
                <li className="flex items-start">
                  <span className="mr-4 text-gray-300">—</span>
                  Using data without losing humanity
                </li>
                <li className="flex items-start">
                  <span className="mr-4 text-gray-300">—</span>
                  Opening doors instead of guarding them
                </li>
                <li className="flex items-start">
                  <span className="mr-4 text-gray-300">—</span>
                  Building community, not just followers
                </li>
              </ul>
            </section>

            {/* Three Pillars */}
            <section className="space-y-20">
              <div className="border-t border-gray-200 pt-8">
                <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">
                  Culture
                </h2>
                <h3 className="text-2xl font-semibold mb-4">DottedMag</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our print publication dedicated to preserving football culture.
                  Through long-form journalism, photography, and storytelling, we document
                  the game beyond the scoreline. DottedMag captures the moments,
                  communities, and traditions that commercialization overlooks.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-8">
                <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">
                  Participation
                </h2>
                <h3 className="text-2xl font-semibold mb-4">Yamka</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Real football, played. Yamka brings the community together through
                  organized sessions where everyone can participate. No academies,
                  no gatekeepers — just the game in its purest form. Book your slot
                  and play.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-8">
                <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">
                  Performance
                </h2>
                <h3 className="text-2xl font-semibold mb-4">Allign</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Performance proof for the modern player. Allign provides analytics
                  and insights that help players understand and improve their game.
                  Data that serves the player, not the other way around.
                </p>
              </div>
            </section>

            {/* Closing */}
            <section className="mt-24 pt-12 border-t border-gray-200">
              <p className="text-lg text-gray-600 leading-relaxed">
                Dottedline builds community through culture, participation, and
                performance proof. We believe football belongs to everyone who
                loves it — and we're working to keep it that way.
              </p>
            </section>

          </div>
        </main>

        <Footer />
      </div>
    </div>
  )
}
